import proj4 from 'proj4';
import { PlaceGeocoding } from '../models/shared/Place';

// SWEREF 99 TM (EPSG:3006) — Sweden's national grid (Lantmäteriet), Easting/Northing
// in meters on the GRS80 ellipsoid, central meridian 15°E, scale factor 0.9996,
// false easting 500000. SWEREF99 and WGS84 coincide to within centimeters, so no
// datum shift is needed.
const SWEREF99TM_DEF = '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs';

type Axis = 'easting' | 'northing';

const directionAxis = (letter: string): Axis | null => {
    switch (letter.toUpperCase()) {
        case 'Ö':
        case 'O': // the Ö diacritic is often dropped when typed or copy-pasted
        case 'V':
            return 'easting';
        case 'N':
        case 'S':
            return 'northing';
        default:
            return null;
    }
};

/**
 * Parses a Swedish SWEREF 99 TM coordinate pair, e.g. "692526,205Ö 6362412,198N",
 * and converts it to WGS84 lat/lng. Order-independent — each number is matched to
 * its axis by its trailing Ö/V (easting) or N/S (northing) letter, not position.
 */
export const parseSweref99TMCoordinates = (query: string): PlaceGeocoding | null => {
    const regex = /(\d+)[.,](\d+)\s*([A-ZÖ])(?![A-ZÖ])/gi;

    let easting: number | null = null;
    let northing: number | null = null;

    for (const match of Array.from(query.matchAll(regex))) {
        const axis = directionAxis(match[3]);
        if (!axis) continue;

        const value = parseFloat(`${match[1]}.${match[2]}`);
        if (axis === 'easting') {
            easting = value;
        } else {
            northing = value;
        }
    }

    if (easting == null || northing == null) {
        return null;
    }

    const [lng, lat] = proj4(SWEREF99TM_DEF, 'WGS84', [easting, northing]);
    return { lat, lng };
};
